import equal from "fast-deep-equal";
import type { Locale } from "../../../../../types";
import { getVisibilityMap } from "../../../../questionnaire/utils/getVisibilityMap";
import type { PartialSection, QuestionnaireState } from "../../../types";
import { flattenSections } from "../groups/flattenSections";
import { mergeState } from "./mergeState";

export const handleMergeAction = (
  state: QuestionnaireState,
  payload: { sections: PartialSection[]; requestStartTime: number },
): QuestionnaireState => {
  const { sections: payloadSections, requestStartTime } = payload;
  const locale = state.config?.locale as Locale | undefined;
  const {
    sections,
    groups,
    visibility: flattenedVisibility,
  } = flattenSections({
    sections: payloadSections,
    answers: state.answers,
    visibility: state.visibility,
  });

  const mergedGroups = { ...state.groups, ...groups };
  const newState = mergeState(state, sections, locale, requestStartTime);
  const visibility = getVisibilityMap({
    sections: newState.sections,
    addressModes: newState.addressModes ?? {},
    groups: mergedGroups, // Include merged groups so we can apply group visibility rules
    visibility: { ...state.visibility, ...flattenedVisibility },
  });

  // Preserve existing sections reference if they're deeply equal to prevent unnecessary re-renders
  const sectionsToUse = equal(state.sections, newState.sections)
    ? state.sections
    : newState.sections;

  // Clamp section indices to valid range, because sections may have been removed
  const maxIndex = Math.max(0, sectionsToUse.length - 1);
  const currentSectionIndex = Math.min(newState.currentSectionIndex, maxIndex);
  const lastViewedSectionIndex = Math.min(
    newState.lastViewedSectionIndex,
    maxIndex,
  );

  return {
    ...newState,
    sections: sectionsToUse,
    groups: mergedGroups,
    visibility,
    currentSectionIndex,
    lastViewedSectionIndex,
  };
};
